import { useLogEntriesBulkDelete } from '@monorepo/logs/src/hooks/useLogEntriesBulkDelete';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { LogEntry } from '@monorepo/shared/componentsV2/logs/types';
import { Main } from '@monorepo/shared/componentsV2/Main';
import { useSkipWidgetLink } from '@monorepo/shared/componentsV2/SkipWidgetLink';
import { BulkDeleteLimitWarning } from '@monorepo/shared/componentsV2/Table/BulkDeleteLimitWarning';
import { Pagination } from '@monorepo/shared/componentsV2/Table/Pagination';
import { getNextSortDirectionForColumn } from '@monorepo/shared/componentsV2/Table/sortUtils';
import { Table } from '@monorepo/shared/componentsV2/Table/Table';
import { TableColumn } from '@monorepo/shared/componentsV2/Table/types';
import { useHandleBulkDelete } from '@monorepo/shared/componentsV2/Table/useHandleBulkDelete';
import { useTableBulkActions } from '@monorepo/shared/componentsV2/Table/useTableBulkActions';
import { useToast } from '@monorepo/shared/contexts';
import { useLogEntries } from '@monorepo/shared/hooks/logs/useLogEntries';
import { useLogEntryFields } from '@monorepo/shared/hooks/logs/useLogFields';
import { useDynamicLinkOnSameProject } from '@monorepo/shared/hooks/navigation/useLinkOnSame/useLinkOnSameProject';
import { useHasProjectUpdatePermissions } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { useDateRangeFilter } from '@monorepo/shared/hooks/useDateRangeFilter';
import { usePrevious } from '@monorepo/shared/hooks/usePrevious';
import { useCurrentUser } from '@monorepo/shared/hooks/useCurrentUser';
import { useProjectUsersById } from '@monorepo/shared/hooks/useProjectUsersById';
import {
  fillHeightAndScrollable,
  verticalLayout,
} from '@monorepo/shared/styles/layout';
import { getUserDisplayName } from '@monorepo/shared/utils/getUserDisplayName';
import { format } from 'date-fns';
import isEqual from 'lodash.isequal';
import {
  localEquivalentOfUTC,
  LogEntryResponse,
  SortDirection,
  UTCEquivalentOfLocal,
} from 'mapistry-shared';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { LogEntryTableActions } from './LogEntryTableActions';
import { LogEntryToolbar } from './LogEntryToolbar';
import {
  LOG_DATE_COLUMN_ID,
  useLogEntryTableColumns,
} from './useLogEntryTableColumns';

const StyledMain = styled(Main)`
  ${verticalLayout}
`;

const StyledTable = styled(Table)`
  ${fillHeightAndScrollable}
` as typeof Table;

type FieldAndColumnIds =
  | {
      columnId: typeof LOG_DATE_COLUMN_ID;
      fieldId: undefined;
    }
  | {
      columnId: string | number;
      fieldId: string;
    };

type LogEntriesSortState = FieldAndColumnIds & { direction: SortDirection };

type LogEntryTableProps = {
  onEditEntry: (logEntryId?: string) => void;
  onUploadEntries: () => void;
};

export function LogEntryTable({
  onEditEntry,
  onUploadEntries,
}: LogEntryTableProps) {
  const { logId, organizationId, pageNumber, projectId } = useParams();
  if (!logId || !organizationId || !projectId) {
    throw new Error(
      "LogEntryTable component was rendered in a page with url that doesn't have logId, or organizationId, or projectId",
    );
  }
  const canSubmitDataForProject = useHasProjectUpdatePermissions(projectId);
  const navigate = useNavigate();
  const { getLinkOnSameProject } = useDynamicLinkOnSameProject();
  const { filter } = useDateRangeFilter();
  const { isLoading: logEntryFieldsLoading, logFields: logEntryFields = [] } =
    useLogEntryFields({
      logId,
      organizationId,
    });

  const [sortState, setSortState] = useState<LogEntriesSortState>();

  const handleSortByColumn = useCallback(
    (column: TableColumn<LogEntryResponse>) => {
      if (!logEntryFields.length) return;

      const columnId = column.id;

      let fieldAndColumnId: FieldAndColumnIds;
      if (columnId === LOG_DATE_COLUMN_ID) {
        fieldAndColumnId = { columnId, fieldId: undefined };
      } else {
        const fieldId = logEntryFields.find(
          (field) => field.columnName === columnId,
        )?.id;
        if (!fieldId) {
          throw new Error(`Field with column name ${columnId} not found`);
        }
        fieldAndColumnId = { columnId, fieldId };
      }

      setSortState((previousState) => {
        const direction = getNextSortDirectionForColumn(
          previousState,
          columnId,
        );
        return direction
          ? {
              ...fieldAndColumnId,
              direction,
            }
          : undefined;
      });
    },
    [logEntryFields],
  );

  const sortProps = useMemo(() => {
    const isSortingByLogDate = sortState?.columnId === LOG_DATE_COLUMN_ID;
    return isSortingByLogDate
      ? {
          logDateSortDirection: sortState.direction,
        }
      : {
          logDateSortDirection: SortDirection.DESC,
          sortingFieldId: sortState?.fieldId,
          sortingFieldDirection: sortState?.direction,
        };
  }, [sortState]);

  const {
    error: logEntryFetchingError,
    isLoading: logEntriesLoading,
    isFetching: logEntriesFetching,
    logEntries,
    pagination,
  } = useLogEntries({
    after: filter?.from ? UTCEquivalentOfLocal(filter.from) : undefined,
    before: filter?.to ? UTCEquivalentOfLocal(filter.to) : undefined,
    logId,
    organizationId,
    projectId,
    requestedPage: Number(pageNumber) || 0,
    ...sortProps,
  });

  const { usersById, isLoading: areUsersLoading } = useProjectUsersById({
    projectId,
    organizationId,
  });

  const { currentUser } = useCurrentUser();

  const { showUserFriendlyErrorToast } = useToast();
  useEffect(() => {
    logEntryFetchingError &&
      showUserFriendlyErrorToast(
        logEntryFetchingError,
        `Couldn't load Log entries`,
      );
    // Not including `showUserFriendlyErrorToast` in useEffect's dependencies,
    // because we need to invoke this effect only when there state changed from no error to error
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logEntryFetchingError]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      // TODO: convert to an absolute link instead of a relative link
      navigate(`../${newPage}`);
    },
    [navigate],
  );

  const previousFilter = usePrevious(filter);
  // Reset table to show first page when filters change
  useEffect(() => {
    // in particular skip resetting the pageNumber on page mount and overruling pageNumber set in the url
    if (isEqual(previousFilter, filter)) return;

    handlePageChange(0);
    // Not including `handlePageChange` in useEffect's dependencies, because we need to invoke this effect only
    // when filter updates. Every time we paginate we navigate to new url, `navigate` object updates,
    // which updates `handlePageChange` and would've invoked this hook too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, previousFilter]);

  const [isFieldDescriptionsShown, setIsFieldDescriptionsShown] =
    useState(false);
  const handleIsFieldDescriptionsShown = useCallback(() => {
    setIsFieldDescriptionsShown((prev) => !prev);
  }, []);

  const {
    getIsRowSelected,
    isBulkActionsModeOn,
    onToggleRow,
    selectedIds,
    setVisibleRows,
  } = useTableBulkActions();
  useEffect(() => {
    setVisibleRows(logEntries || []);
  }, [logEntries, setVisibleRows]);

  const getCheckboxAriaLabel = useCallback(
    ({ logDate }: LogEntryResponse) =>
      `Select Log entry on ${format(
        localEquivalentOfUTC(new Date(logDate)),
        'Pp',
      )}`,
    [],
  );

  const handleOpenExistingAiSubmissionsModal = useCallback(
    (edpAiSubmissionId: string, logEntryId: string) => {
      navigate(
        getLinkOnSameProject(
          `/logs/${logId}/records/ai-submission/${edpAiSubmissionId}`,
        ),
        {
          state: {
            logEntryId,
            returnPageNumber: Number(pageNumber) || 0,
          },
        },
      );
    },
    [getLinkOnSameProject, logId, navigate, pageNumber],
  );

  const hasAiReviewRows = (logEntries ?? []).some(
    (entry) => entry.edpAiSubmissionId && !entry.reviewedBy,
  );

  const baseColumns = useLogEntryTableColumns({
    logEntries,
    logEntryFields,
    isBulkActionsModeOn,
    isFieldDescriptionsShown,
    handleOpenExistingAiSubmissionsModal,
  });

  const createdByColumnContent = useCallback(
    (logEntry: LogEntryResponse) =>
      areUsersLoading ? (
        <Loading size="small" />
      ) : (
        getUserDisplayName(usersById?.[logEntry.createdBy], currentUser) ||
        'Unknown user'
      ),
    [areUsersLoading, usersById, currentUser],
  );

  const createdByColumn = useMemo(
    () => ({
      id: 'createdBy',
      header: 'Created By',
      contents: createdByColumnContent,
      width: '10%',
    }),
    [createdByColumnContent],
  );

  const actionsColumnContent = useCallback(
    (logEntry: LogEntry) => (
      <LogEntryTableActions
        alignMenusToEnd={hasAiReviewRows}
        logEntry={logEntry}
        onEditEntry={onEditEntry}
        onViewEntrySource={handleOpenExistingAiSubmissionsModal}
      />
    ),
    [handleOpenExistingAiSubmissionsModal, hasAiReviewRows, onEditEntry],
  );

  const actionsColumn: TableColumn<LogEntry> | undefined = useMemo(
    () =>
      canSubmitDataForProject && !isBulkActionsModeOn
        ? {
            id: 'actions',
            header: 'Actions',
            contents: actionsColumnContent,
            align: hasAiReviewRows ? 'right' : 'center',
            width: '10%',
          }
        : undefined,
    [
      canSubmitDataForProject,
      actionsColumnContent,
      hasAiReviewRows,
      isBulkActionsModeOn,
    ],
  );

  const allColumns = useMemo(
    () => [
      ...baseColumns,
      createdByColumn,
      ...(actionsColumn ? [actionsColumn] : []),
    ],
    [baseColumns, actionsColumn, createdByColumn],
  );

  const [bulkDeleter] = useLogEntriesBulkDelete({
    config: { throwOnError: true },
  });
  const onBulkDelete = useCallback(
    async () =>
      bulkDeleter({
        logEntryIds: selectedIds,
        logId,
        organizationId,
        projectId,
      }),
    [bulkDeleter, selectedIds, logId, organizationId, projectId],
  );
  const { handleBulkDeleteClick, isBulkDeleting } = useHandleBulkDelete({
    onBulkDelete,
    pluralEntityLabel: 'Log entries',
  });

  const { SkipLink, SkipLinkTarget } = useSkipWidgetLink();

  if (logEntriesLoading || logEntriesFetching || logEntryFieldsLoading) {
    return <Loading />;
  }

  return (
    <StyledMain>
      <LogEntryToolbar
        isBulkDeleting={isBulkDeleting}
        logId={logId}
        onAddNew={() => onEditEntry()}
        onBulkDelete={handleBulkDeleteClick}
        onUpload={onUploadEntries}
        organizationId={organizationId}
        projectId={projectId}
        isFieldDescriptionsShown={isFieldDescriptionsShown}
        handleIsFieldDescriptionsShown={handleIsFieldDescriptionsShown}
      />
      <BulkDeleteLimitWarning pluralEntityLabel="Log entries" />
      <SkipLink linkText="Skip to pagination" />
      <StyledTable
        ariaLabel="All Log entries"
        canRowsBeSelected={isBulkActionsModeOn}
        columns={allColumns}
        getCheckboxAriaLabel={getCheckboxAriaLabel}
        getIsRowSelected={getIsRowSelected}
        isRowSelectionDisabled={isBulkDeleting}
        onSortByColumn={handleSortByColumn}
        onToggleRow={onToggleRow}
        rows={logEntries || []}
        sortState={sortState}
      />
      <SkipLinkTarget />
      <Pagination
        onPageChange={handlePageChange}
        page={pagination?.currentPage || 0}
        rowsPerPage={pagination?.perPage}
        totalRowCount={pagination?.fullCount}
      />
    </StyledMain>
  );
}
